import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Polyline } from "@tbm/shared";
import { DrawingCanvas, type PolylineLayer } from "./Drawing/DrawingCanvas";
import {
  loadDrawingFromFile,
  loadDrawingFromUrl,
  type DrawingPayload,
} from "./Drawing/loadDrawing";

const PLAN_COLOR = "#c8102e";       // Ward & Burke red
const ELEVATION_COLOR = "#1d4ed8";  // strong blue

type TargetId = "plan" | "elevation";

interface Props {
  liveChainageM: number | null;
  driveLengthM: number | null;
}

export function DrawingPanel({ liveChainageM, driveLengthM }: Props) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);

  const drawingQuery = useQuery({
    queryKey: ["drawing"],
    queryFn: api.drawing,
    refetchInterval: 60_000,
  });

  const [payload, setPayload] = useState<DrawingPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Edit state (lifted up from the old AlignmentEditor).
  const [editing, setEditing] = useState(false);
  const [target, setTarget] = useState<TargetId>("plan");
  const [plan, setPlan] = useState<Polyline>([]);
  const [elevation, setElevation] = useState<Polyline>([]);

  const updatedAt = drawingQuery.data?.updatedAt ?? "";
  const hasDrawing = !!drawingQuery.data?.hasDrawing;
  const mime = drawingQuery.data?.mime ?? null;

  // Sync local polylines from the server query — but never while the
  // operator is mid-edit. This query refetches every 60 s, so a save by
  // anyone else (or any change in object identity) silently discarded
  // the points they had just clicked. Cancel/Save re-sync explicitly.
  useEffect(() => {
    if (editing) return;
    setPlan(drawingQuery.data?.planPolyline ?? []);
  }, [drawingQuery.data?.planPolyline, editing]);
  useEffect(() => {
    if (editing) return;
    setElevation(drawingQuery.data?.elevationPolyline ?? []);
  }, [drawingQuery.data?.elevationPolyline, editing]);

  // Load (or render) the drawing payload from the server.
  useEffect(() => {
    let cancelled = false;
    if (!hasDrawing || !mime) {
      setPayload(null);
      return () => { cancelled = true; };
    }
    const url = `/api/drawing/image?v=${encodeURIComponent(updatedAt)}`;
    setIsLoading(true);
    setLoadError(null);
    loadDrawingFromUrl(url, mime)
      .then((p) => {
        if (!cancelled) setPayload(p);
      })
      .catch((err: Error) => {
        if (!cancelled) setLoadError(err.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasDrawing, mime, updatedAt]);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const local = await loadDrawingFromFile(file);
      setPayload((prev) => {
        // Raster uploads hand back an object URL that stays alive for
        // the <img>; release the previous one so repeated uploads don't
        // pin every earlier file in memory.
        if (prev?.imageUrl.startsWith("blob:")) URL.revokeObjectURL(prev.imageUrl);
        return local;
      });
      return api.uploadDrawing(file);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["drawing"] }),
    onError: (err: Error) => setLoadError(err.message),
  });

  const saveAlignmentMutation = useMutation({
    mutationFn: async ({ plan: p, elevation: e }: { plan: Polyline | null; elevation: Polyline | null }) =>
      api.saveAlignment(p, e),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["drawing"] }),
  });

  function pickFile() {
    fileRef.current?.click();
  }
  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadMutation.mutate(file);
    e.target.value = "";
  }

  function handleClick(point: [number, number]) {
    if (!editing) return;
    if (target === "plan") setPlan((p) => [...p, point]);
    else setElevation((p) => [...p, point]);
  }
  function undo() {
    if (target === "plan") setPlan((p) => p.slice(0, -1));
    else setElevation((p) => p.slice(0, -1));
  }
  function clearActive() {
    if (target === "plan") setPlan([]);
    else setElevation([]);
  }
  async function save() {
    await saveAlignmentMutation.mutateAsync({
      plan: plan.length ? plan : null,
      elevation: elevation.length ? elevation : null,
    });
    setEditing(false);
  }
  function cancel() {
    setPlan(drawingQuery.data?.planPolyline ?? []);
    setElevation(drawingQuery.data?.elevationPolyline ?? []);
    setEditing(false);
  }

  const fraction =
    liveChainageM != null && driveLengthM != null && driveLengthM > 0
      ? Math.max(0, Math.min(1, liveChainageM / driveLengthM))
      : null;

  const layers: PolylineLayer[] = [
    {
      points: plan,
      color: PLAN_COLOR,
      markerFraction: editing ? null : fraction,
      driveLengthM,
    },
    {
      points: elevation,
      color: ELEVATION_COLOR,
      markerFraction: editing ? null : fraction,
      driveLengthM,
    },
  ];

  const activePts = target === "plan" ? plan : elevation;
  const saving = saveAlignmentMutation.isPending;

  return (
    <section className="card flex flex-col h-full overflow-hidden">
      <div className="flex items-center gap-2 px-3 h-12 border-b border-border1 flex-nowrap overflow-x-auto">
        <h3 className="text-[12px] font-semibold tracking-[0.18em] uppercase text-text whitespace-nowrap">
          Alignment
        </h3>
        {driveLengthM != null && (
          <span className="hidden md:inline text-[11px] text-muted ml-1 whitespace-nowrap">
            <span className="uppercase tracking-wider text-dim mr-1">
              Drive length
            </span>
            <span className="mono-nums text-text">{driveLengthM}</span>
            <span className="text-dim ml-0.5">m</span>
          </span>
        )}
        <div className="flex-1 min-w-0" />

        {!editing && drawingQuery.data?.updatedAt && (
          <span className="hidden lg:inline text-[10.5px] text-dim mono-nums whitespace-nowrap">
            updated {new Date(drawingQuery.data.updatedAt).toLocaleString()}
          </span>
        )}

        {!editing ? (
          <>
            <button
              onClick={pickFile}
              disabled={uploadMutation.isPending}
              className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded shrink-0 whitespace-nowrap border border-border2 text-text hover:border-wb hover:text-wb disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {hasDrawing ? "Replace drawing" : "Upload drawing"}
            </button>
            <button
              onClick={() => setEditing(true)}
              disabled={!payload}
              className={clsx(
                "text-[11px] uppercase tracking-wider px-3 py-1.5 rounded shrink-0 whitespace-nowrap border",
                payload
                  ? "border-border2 text-text hover:border-wb hover:text-wb"
                  : "border-border1 text-dim cursor-not-allowed",
              )}
            >
              Edit alignment
            </button>
          </>
        ) : (
          <>
            <Segmented
              value={target}
              onChange={setTarget}
              options={[
                { id: "plan",      label: "Plan",      color: PLAN_COLOR },
                { id: "elevation", label: "Elevation", color: ELEVATION_COLOR },
              ]}
            />
            <button
              onClick={undo}
              disabled={activePts.length === 0}
              className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded shrink-0 whitespace-nowrap border border-border2 text-text hover:border-wb hover:text-wb disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Undo
            </button>
            <button
              onClick={clearActive}
              disabled={activePts.length === 0}
              className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded shrink-0 whitespace-nowrap border border-border2 text-text hover:border-wb hover:text-wb disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Clear
            </button>
            <span className="text-[11px] text-muted ml-1 mono-nums">
              {activePts.length} pt{activePts.length === 1 ? "" : "s"}
            </span>
            <button
              onClick={cancel}
              className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded shrink-0 whitespace-nowrap border border-border2 text-muted hover:text-text hover:border-border2"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded shrink-0 whitespace-nowrap bg-wb text-white hover:bg-wbHot disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </>
        )}

        <input
          ref={fileRef}
          type="file"
          accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf"
          onChange={handleFile}
          className="hidden"
        />
      </div>

      <div className="flex-1 min-h-0 relative bg-white">
        {!hasDrawing && !uploadMutation.isPending && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-surface1">
            <div className="text-[13px] text-text font-semibold mb-1">No drawing uploaded yet</div>
            <div className="text-[12px] text-muted mb-4 max-w-sm">
              Upload a single drawing (PNG, JPG, or PDF) containing both plan and elevation views.
              Then click Edit alignment to trace the polylines.
            </div>
            <button
              onClick={pickFile}
              className="text-[11px] uppercase tracking-wider px-4 py-2 rounded bg-wb text-white hover:bg-wbHot"
            >
              Upload drawing
            </button>
          </div>
        )}

        {(isLoading || uploadMutation.isPending) && (
          <div className="absolute inset-0 flex items-center justify-center text-[12px] text-muted z-10 pointer-events-none">
            {uploadMutation.isPending ? "Uploading…" : "Loading drawing…"}
          </div>
        )}

        {loadError && (
          <div className="absolute top-2 left-2 right-2 text-[12px] text-wb bg-white border border-wb/40 rounded p-2 z-20">
            {loadError}
          </div>
        )}

        {hasDrawing && (
          <DrawingCanvas
            payload={payload}
            layers={layers}
            onClick={handleClick}
            cursorMode={editing ? "edit" : "view"}
          />
        )}
      </div>
    </section>
  );
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { id: T; label: string; color?: string }[];
}) {
  return (
    <div className="inline-flex bg-surface2 border border-border1 rounded overflow-hidden">
      {options.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={clsx(
            "px-3 py-1.5 text-[11px] tracking-wider uppercase transition-colors flex items-center gap-2",
            value === o.id ? "bg-wb text-white" : "text-muted hover:bg-surface3 hover:text-text",
          )}
        >
          {o.color && (
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ background: o.color }}
            />
          )}
          {o.label}
        </button>
      ))}
    </div>
  );
}

